import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Button, Typography, Tooltip } from '@mui/material';
import ReactLoading from 'react-loading';
import ReactTable from 'react-table';

import Actions from './Actions';
import MessageButton from '../MessageButton';
import Page from '../../../Page';
import Alert from '../../../../ui/Alert';

import { fetchSingleDispute } from '../../../../modules/disputes/thunks';
import { cfPath } from '../../../../utilities/UrlPaths';
import { BACK_TO_VIEW_DISPUTES, VIEW_DISPUTE_URL, formatPhoneNumber } from '../utils';
import { CLEAR_DISPUTE_MESSAGE_DATA } from '../../../../modules/disputes/reducer';

const SingleDispute = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { disputeRef } = useParams();

  const { houseRef } = useSelector(store => store.house) || {};
  const singleDisputeData = useSelector(store => store.disputes.singleDisputeData) || {};
  const loading = useSelector(store => store.disputes.fetchSingleDisputeLoading) || false;
  const submitSuccessMessage = useSelector(store => store.disputes.submitSuccessMessage) || '';
  const sendDisputeMessageData =
    useSelector(store => store.disputes.confirmDisputeMessageData) || {};

  const { success } = sendDisputeMessageData;
  const isMessageSend = success === 'true';
  const hasValidDisputeData = Boolean(Object.keys(singleDisputeData).length);
  const catalog = hasValidDisputeData ? singleDisputeData.catalogInfo : {};
  const disputeInfo = hasValidDisputeData ? singleDisputeData.disputeInfo : {};
  const heading = hasValidDisputeData ? `${catalog.catalogTitle}, ${catalog.catalogDate}` : '';
  const isOpenDispute = hasValidDisputeData ? disputeInfo.openDispute : false;

  const column = [
    {
      Header: 'Buyer',
      id: ' buyer',
      width: 200,
      sortable: false,
      Cell: ({ original: { bidderInfo } }) => {
        const { firstName = '', lastName = '', channel = '', approvalID, phone = '' } =
          bidderInfo || {};
        const channelLabel = channel ? `(${channel})` : '';
        const formattedPhoneNumber = formatPhoneNumber(phone);
        const label = `${lastName}, ${firstName} ${channelLabel} ${formattedPhoneNumber}`;
        return (
          <Tooltip title={label}>
            <a
              title={label}
              className="dispute-table-data"
              href={`${cfPath()}/al/bidders/viewBidder.cfm?approvalID=${approvalID}`}
            >
              {label}
            </a>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Open Date',
      id: 'openDate',
      width: 100,
      sortable: false,
      Cell: ({ original: { disputeInfo } }) => (
        <Tooltip title={disputeInfo.openDate}>
          <span>{disputeInfo.openDate}</span>
        </Tooltip>
      )
    },
    {
      Header: 'Lot',
      id: 'lot',
      width: 200,
      sortable: false,
      Cell: ({ original: { lotInfo } }) => {
        const { lotNumber, lotTitle, lotURL } = lotInfo || {};
        const label = `${lotNumber}: ${lotTitle}`;
        return (
          <Tooltip title={label}>
            <a className="dispute-table-data" href={lotURL} target="_blank" rel="noreferrer">
              {label}
            </a>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Hammer',
      id: 'hammer',
      width: 80,
      sortable: false,
      Cell: ({ original: { priceInfo } }) => {
        const { currencySymbol = '', hammer = '' } = priceInfo || {};
        const label = `${currencySymbol}${hammer}`;
        return (
          <Tooltip title={label}>
            <span className="dispute-table-data">{label}</span>
          </Tooltip>
        );
      }
    },
    {
      Header: 'BP',
      id: 'bp',
      width: 80,
      sortable: false,
      Cell: ({ original: { priceInfo } }) => {
        const { currencySymbol = '', bp = '' } = priceInfo || {};
        const label = `${currencySymbol}${bp}`;
        return (
          <Tooltip title={label}>
            <span className="dispute-table-data">{label}</span>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Live fee',
      id: 'liveFee',
      width: 80,
      sortable: false,
      Cell: ({ original: { priceInfo } }) => {
        const { currencySymbol = '', liveFee = '' } = priceInfo || {};
        const label = `${currencySymbol}${liveFee}`;
        return (
          <Tooltip title={label}>
            <span className="dispute-table-data">{label}</span>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Payable',
      id: 'payable',
      width: 80,
      sortable: false,
      Cell: ({ original: { priceInfo } }) => {
        const { currencySymbol = '', payable = '' } = priceInfo || {};
        const label = `${currencySymbol}${payable}`;
        return (
          <Tooltip title={label}>
            <span className="dispute-table-data">{label}</span>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Reason',
      accessor: 'disputeInfo.disputeReason',
      id: 'reason',
      width: 200,
      sortable: false,
      Cell: ({ original: { disputeInfo } }) => (
        <Tooltip title={disputeInfo.disputeReason}>
          <span className="dispute-table-data" title={disputeInfo.disputeReason}>
            {disputeInfo.disputeReason}
          </span>
        </Tooltip>
      )
    },
    {
      Header: 'Status',
      accessor: 'disputeInfo.disputeStat',
      id: 'status',
      sortable: false,
      Cell: ({ original: { disputeInfo } }) => (
        <Tooltip title={disputeInfo.disputeStat}>
          <span className="dispute-table-data" title={disputeInfo.disputeStat}>
            {disputeInfo.disputeStat}
          </span>
        </Tooltip>
      )
    },
    {
      Header: 'Message',
      id: 'message',
      sortable: false,
      width: 80,
      Cell: ({ original }) => <MessageButton disputeData={original} />
    }
  ];

  useEffect(() => {
    if (disputeRef) dispatch(fetchSingleDispute(houseRef, disputeRef));
  }, [houseRef, disputeRef]);

  return (
    <Page
      header={{ title: 'View Single Dispute' }}
      navLink={{
        displayName: BACK_TO_VIEW_DISPUTES,
        href: VIEW_DISPUTE_URL
      }}
    >
      {!houseRef || loading ? (
        <Box display="flex" direction="column" alignItems="center" justifyContent="center">
          <ReactLoading type="spin" color="black" />
        </Box>
      ) : (
        <Box>
          {hasValidDisputeData ? (
            <Box>
              <Typography variant="body1" mb={2} fontWeight="bold">
                {heading}
              </Typography>
              {!isOpenDispute && isMessageSend ? (
                <Box>
                  <Alert isOpen>
                    <Typography variant="body2" fontWeight="bold">
                      {submitSuccessMessage}
                    </Typography>
                  </Alert>
                  <Button
                    sx={{ mt: 2 }}
                    className="submit-action"
                    variant="contained"
                    onClick={() => {
                      history.push(VIEW_DISPUTE_URL);
                    }}
                  >
                    Go Back
                  </Button>
                </Box>
              ) : (
                <Box>
                  <ReactTable
                    columns={column}
                    data={[singleDisputeData]}
                    pageSize={1}
                    minRows={0}
                    style={{ width: '100%', maxHeight: '400px' }}
                    showPagination={false}
                    className="-highlight dispute-table"
                  />
                  {isMessageSend && (
                    <Box mt={2} mb={2}>
                      <Alert
                        isOpen
                        onClose={() => {
                          dispatch({ type: CLEAR_DISPUTE_MESSAGE_DATA });
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {submitSuccessMessage}
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                  <Actions dispute={singleDisputeData} isOpenDispute={isOpenDispute} />
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="h6" color="red">
              No Disputes Exist
            </Typography>
          )}
        </Box>
      )}
    </Page>
  );
};

export default SingleDispute;
